import {Alert, Button, Group, Stack, Text, Title} from '@mantine/core'
import {Link} from '@tanstack/react-router'
import {useMemo, useState} from 'react'

import {useRentalAssetTypes} from 'src/assets/rental-assets/use-rental-asset-types'
import {useWorkerTypes} from 'src/assets/worker-types/use-worker-types'
import {formatPrice} from 'src/lib/format-price'

import {draftRequirementsToWriteInput, quoteToDraft} from './quote-adapters'
import {type QuoteDraft, quoteSubtotal} from './quote-builder-logic'
import {QuoteRequirementsByLine} from './quote-requirements-by-line'
import {QuoteStatusBadge, quoteStatusLabel} from './quote-status-badge'
import {useReplaceQuoteRequirements} from './use-quotes'

type QuoteRequirementsPageProps = {
    quoteId: string
    initialDraft: QuoteDraft
}

function requirementsSubtitle(status: QuoteDraft['status'], isSaving: boolean): string {
    if (status === 'accepted') {
        return isSaving ? 'Saving requirements…' : 'Use Save to persist requirement changes'
    }

    if (status === 'draft' || status === 'sent') {
        return 'View-only until the quote is accepted'
    }

    return 'Requirement mapping is read-only for this quote status'
}

export function QuoteRequirementsPage({quoteId, initialDraft}: QuoteRequirementsPageProps) {
    const [quote, setQuote] = useState<QuoteDraft>(() => initialDraft)
    const {data: workerTypes = [], isLoading: isLoadingWorkerTypes} = useWorkerTypes()
    const {data: rentalAssetTypes = [], isLoading: isLoadingRentalAssetTypes} = useRentalAssetTypes(
        {includeArchived: true},
    )
    const replaceRequirementsMutation = useReplaceQuoteRequirements()
    const [saveError, setSaveError] = useState<string | null>(null)

    const canEditRequirements = quote.status === 'accepted'
    const isSaving = replaceRequirementsMutation.isPending
    const isLoadingTypes = isLoadingWorkerTypes || isLoadingRentalAssetTypes

    const subtotal = useMemo(
        () => quoteSubtotal(quote.lines, quote.meta.rentalPeriod),
        [quote.lines, quote.meta.rentalPeriod],
    )

    async function handleSave() {
        if (!canEditRequirements) {
            return
        }

        setSaveError(null)
        try {
            const updated = await replaceRequirementsMutation.mutateAsync({
                id: quoteId,
                requirements: draftRequirementsToWriteInput(quote.requirements),
            })
            setQuote(quoteToDraft(updated))
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : 'Failed to save requirements')
        }
    }

    return (
        <Stack gap="lg">
            <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                    <Group gap="sm" align="center">
                        <Title order={1}>Quote requirements</Title>
                        <QuoteStatusBadge status={quote.status} />
                    </Group>
                    <Text size="sm" c="dimmed">
                        {requirementsSubtitle(quote.status, isSaving)}
                    </Text>
                </Stack>
                <Stack gap={2} align="flex-end">
                    <Text size="sm" c="dimmed">
                        Total
                    </Text>
                    <Text size="xl" fw={700}>
                        {formatPrice(subtotal)}
                    </Text>
                    {canEditRequirements ? (
                        <Button onClick={() => void handleSave()} loading={isSaving}>
                            Save
                        </Button>
                    ) : null}
                </Stack>
            </Group>

            {quote.status === 'draft' || quote.status === 'sent' ? (
                <Alert title="Requirement mapping is not available yet" color="blue">
                    You can review this quote&apos;s lines here, but requirements cannot be added or
                    changed until the quote is accepted. Return to the{' '}
                    <Text
                        component={Link}
                        to="/quotes/$quoteId"
                        params={{quoteId}}
                        fw={600}
                        inherit
                    >
                        line items page
                    </Text>{' '}
                    and accept the quote to enable mapping.
                </Alert>
            ) : null}

            {quote.status === 'rejected' || quote.status === 'voided' ? (
                <Alert title="Requirement mapping is read-only" color="gray">
                    This quote is {quoteStatusLabel(quote.status).toLowerCase()} and its
                    requirements cannot be changed.
                </Alert>
            ) : null}

            {saveError ? (
                <Text size="sm" c="red">
                    {saveError}
                </Text>
            ) : null}

            <Button
                component={Link}
                to="/quotes/$quoteId"
                params={{quoteId}}
                variant="subtle"
                size="compact-sm"
                w="fit-content"
            >
                Back to line items
            </Button>

            <QuoteRequirementsByLine
                lines={quote.lines}
                requirements={quote.requirements}
                workerTypes={workerTypes}
                rentalAssetTypes={rentalAssetTypes}
                readOnly={!canEditRequirements || isLoadingTypes}
                rentalPeriod={quote.meta.rentalPeriod}
                onRequirementsChange={(requirements) =>
                    setQuote((current) => ({...current, requirements}))
                }
            />

            <Button
                component={Link}
                to="/quotes/$quoteId/scheduling"
                params={{quoteId}}
                variant="light"
                w="fit-content"
            >
                Continue to scheduling
            </Button>
        </Stack>
    )
}
